/**
 * UsageMeter — one labeled meter (used-by / limit) over the shared track.
 * CLS-safe: the value line and the track reserve their height before data
 * lands, so a slow read never shifts the layout. Fill color escalates
 * warn (≥80%) → danger (100%) from the shared palette only.
 */
import { formatMeterValue, formatResetAt, formatWindow } from "../format";
import { useBillingLabels } from "../strings";
import type { UsageMeterData } from "../types";

export interface UsageMeterProps {
  label: string;
  meter: UsageMeterData;
  /** Hide the reset footnote (compact contexts). */
  compact?: boolean;
}

export function UsageMeter({ label, meter, compact }: UsageMeterProps) {
  const labels = useBillingLabels();
  const used = formatMeterValue(meter.used, meter.unit);
  const limit = formatMeterValue(meter.limit, meter.unit);

  // A policy cap (max_file_bytes) renders the limit, not a consumption.
  const unlimited = meter.source === "policy";
  const ratio = meter.limit > 0 ? Math.min(1, meter.used / meter.limit) : 0;
  const over = !unlimited && meter.used >= meter.limit;
  const warn = !unlimited && ratio >= 0.8 && !over;
  const fillClass = over
    ? " suite-billing-meter-fill--danger"
    : warn
      ? " suite-billing-meter-fill--warn"
      : "";

  const headText = unlimited
    ? labels.meterUnlimited(limit)
    : over
      ? labels.meterOverLimit
      : labels.meterOf(used, limit);

  return (
    <div className="suite-billing-meter" role="img" aria-label={`${label}: ${headText}`}>
      <div className="suite-billing-meter-head">
        <span className="suite-billing-meter-label">{label}</span>
        <span className="suite-billing-meter-value">{headText}</span>
      </div>
      <div className="suite-billing-meter-track">
        {/* The fill width is a runtime calculation from live data — the one
            inline style this kit allows (workspace rule: dynamic values only). */}
        <div className={`suite-billing-meter-fill${fillClass}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
      </div>
      {!compact && (
        <div className={`suite-billing-meter-foot${meter.degraded ? " suite-billing-meter-foot--degraded" : ""}`}>
          {meter.degraded
            ? labels.meterDegraded
            : meter.source === "policy"
              ? ""
              : meter.resetAt
                ? `${formatWindow(meter.window)} · ${labels.resetsOn(formatResetAt(meter.resetAt))}`
                : formatWindow(meter.window)}
        </div>
      )}
    </div>
  );
}
